import { Breadcrumb, Button, Card, TextInput } from "pepsico-ds";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { paths } from "../../../routes/paths";
import WidgetDragDropSection from "../ConfigurationDrawer/WidgetDragDropSection";
import { SystemCard } from "./SystemCard/SystemCard";
import "./WidgetDetailPage.scss";

const WIDGET_STATIC_DATA = {
  survey_card: { name: "Survey/Quiz card" },
  email_card: { name: "Email Card" },
  reward_card: { name: "Reward Card" },
  profile_card: { name: "Profile Card" },
  po1_pending_completion_card: { name: "PO1 Pending Completion Card" },
  collect_and_win_pending_completion_card: {
    name: "Collect & Win Pending Completion Card",
  },
};

const EXCLUDED_CARDS = [
  "collect_and_win_pending_completion_card",
  "po1_pending_completion_card",
];

/**
 * Builds a sorted list of visible widget keys from the widget data object.
 */
function getVisibleWidgetList(widgetData) {
  if (!widgetData) return [];
  return Object.entries(widgetData)
    .filter(([, val]) => val?.display && val?.position > 0)
    .sort((a, b) => (a[1]?.position > b[1]?.position ? 1 : -1))
    .map(([key]) => key);
}

function getConfiguredWidgetList(widgetData) {
  return Object.entries(widgetData || {})
    .filter(([, widget]) => typeof widget?.display === "boolean")
    .sort(
      (a, b) =>
        (Number(a[1]?.position) || Number.MAX_SAFE_INTEGER) -
        (Number(b[1]?.position) || Number.MAX_SAFE_INTEGER)
    )
    .map(([key]) => key);
}

/**
 * Reusable widget detail content (Display Order + System Cards + Days to show).
 * Used both on the full WidgetDetailPage and inside the configuration drawer.
 *
 * @param {Object} props
 * @param {Object} props.widgetData - Widget configuration data object
 */
export const WidgetDetailContent = ({
  widgetData,
  variant = "page",
  isEdit = false,
  onChange,
}) => {
  const list = getVisibleWidgetList(widgetData);

  if (variant === "drawer") {
    const drawerList = isEdit ? getConfiguredWidgetList(widgetData) : list;
    const widgetsWithDays = drawerList.filter(
      (widgetName) =>
        !EXCLUDED_CARDS.includes(widgetName) &&
        widgetData[widgetName]?.number_of_days_to_show !== undefined
    );

    return (
      <div className="widgetDetailPage widgetDetailPage--drawer">
        <Card className="widgets-view-section">
          <h3>Display Order</h3>
          <div>
            {list.length > 0 || isEdit ? (
              <WidgetDragDropSection
                disabled={!isEdit}
                formData={widgetData || {}}
                onReorder={onChange || (() => {})}
              />
            ) : (
              <p className="widgets-view-empty">No widgets are enabled.</p>
            )}
          </div>
        </Card>

        <Card className="widgets-view-section">
          <h3>Number of days to show</h3>
          <div className="widgets-view-days-list">
            {widgetsWithDays.length > 0 ? (
              widgetsWithDays.map((widgetName) => (
                <TextInput
                  disabled={!isEdit || !widgetData[widgetName]?.display}
                  key={widgetName}
                  label={WIDGET_STATIC_DATA[widgetName]?.name || widgetName}
                  min={widgetName === "reward_card" ? 1 : 0}
                  onUpdate={(value) => {
                    const parsedValue = value === "" ? "" : Number(value);
                    onChange?.({
                      ...widgetData,
                      [widgetName]: {
                        ...widgetData[widgetName],
                        number_of_days_to_show: parsedValue,
                      },
                    });
                  }}
                  type="number"
                  value={widgetData[widgetName].number_of_days_to_show}
                />
              ))
            ) : (
              <p className="widgets-view-empty">
                No display duration is configured.
              </p>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="widgetDetailPage">
      <div className="summary-mod">
        <Card className="basic-setup">
          <h3>Display Order</h3>
          <div className="summary-content-wrapper">
            {list.map((widgetName) => (
              <div key={widgetName} className="summary-content">
                <p className="title">{WIDGET_STATIC_DATA[widgetName]?.name}</p>
                <p className="content">{widgetData[widgetName]?.position || "-"}</p>
              </div>
            ))}
          </div>
          <div className="system-card-wrapper">
            {list.map((widgetName) => (
              <SystemCard
                data-testid="system-card"
                key={widgetName}
                widgetName={widgetName}
              />
            ))}
          </div>
        </Card>
        <Card className="basic-setup">
          <h3>Number of days to show</h3>
          <div className="summary-content-wrapper">
            {list.map((widgetName) => {
              if (
                !EXCLUDED_CARDS.includes(widgetName) &&
                widgetData[widgetName]?.number_of_days_to_show !== undefined
              ) {
                return (
                  <div key={widgetName} className="summary-content">
                    <p className="title">{WIDGET_STATIC_DATA[widgetName]?.name}</p>
                    <p className="content">
                      {widgetData[widgetName]?.number_of_days_to_show}
                    </p>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

WidgetDetailContent.propTypes = {
  widgetData: PropTypes.object,
  variant: PropTypes.oneOf(["page", "drawer"]),
  isEdit: PropTypes.bool,
  onChange: PropTypes.func,
};

function WidgetDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location?.state?.data[0];

  const handleEdit = () => {
    navigate(`../../${paths.system_setting}`, {
      state: { editWidget: true, data: data },
    });
  };

  const handleCancel = () => {
    navigate(`../../${paths.system_setting}`);
  };

  return (
    <div className="widgetDetailPage">
      <Breadcrumb
        breadcrumbs={[
          {
            label: "Program Settings",
            href: `../../${paths.system_setting}`,
          },
          { label: `${data?.name} Details` },
        ]}
        activeIndex={2}
      />
      <WidgetDetailContent widgetData={data?.data} />
      <div
        className="bottom-button"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button variant="outline" color="#fff" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleEdit}>Edit</Button>
      </div>
    </div>
  );
}

export default WidgetDetailPage;
